import { useCallback, useMemo } from 'react';
import { useLocation, useSearch } from 'wouter';

/**
 * Every URL in the app, built in one place. Pages are paths; what's open on a page (a drawer, a dialog, a sheet,
 * a spot on the flow) is query state, so every view can be linked to and Back closes what was just opened.
 */
export type RoundTab = 'flow' | 'decide' | 'ballot';
export const ROUND_TABS: RoundTab[] = ['flow', 'decide', 'ballot'];
/** Tabs (account, display, timer, data, tabroom) plus older per-section addresses that open the Data tab. */
export const SETTINGS_SECTIONS = ['account', 'tabroom', 'display', 'timer', 'data', 'privacy', 'backup', 'storage'] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

const q = (params?: Record<string, string | null | undefined>) => {
  const s = new URLSearchParams(Object.entries(params ?? {}).filter((e): e is [string, string] => !!e[1])).toString();
  return s ? `?${s}` : '';
};

export const paths = {
  splash: '/',
  dashboard: '/app',
  tournament: (tid: string, params?: Record<string, string | null>) => `/t/${tid}${q(params)}`,
  round: (tid: string, rid: string, tab: RoundTab = 'flow', params?: Record<string, string | null>) => `/t/${tid}/r/${rid}/${tab}${q(params)}`,
  settings: (section?: SettingsSection) => (section ? `/settings/${section}` : '/settings'),
};

/** History entries we pushed to open something, so closing it can go Back instead of piling up entries. */
const OPENED = 'flowOpened';

/** Query-string state for the current page: e.g. `?panel=notes`, `?modal=new-round`, `?sheet=case-first`. */
export const useQueryState = () => {
  const search = useSearch();
  const [location, navigate] = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  /**
   * Change some query values (null removes one). By default this adds a history entry, so Back undoes it;
   * `replace` doesn't (for frequent changes like switching sheets or typing a search).
   */
  const set = useCallback((changes: Record<string, string | null>, opts: { replace?: boolean } = {}) => {
    const next = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    const qs = next.toString();
    const replace = opts.replace ?? false;
    navigate(`${location}${qs ? `?${qs}` : ''}`, { replace, state: replace ? window.history.state : { [OPENED]: Object.keys(changes) } });
  }, [location, navigate]);
  /** Close something opened with `set`: Back if that's how we got here, otherwise drop the keys in place. */
  const close = useCallback((...keys: string[]) => {
    const opened = (window.history.state as Record<string, unknown> | null)?.[OPENED];
    if (Array.isArray(opened) && keys.some((k) => opened.includes(k))) window.history.back();
    else set(Object.fromEntries(keys.map((k) => [k, null])), { replace: true });
  }, [set]);
  return { get: (k: string) => params.get(k), set, close, location, navigate };
};
